import { useContext, useEffect, useState, useMemo } from "react";
import { DasboardContext } from "../../context/dashboard-context";
import { useParams } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { Button } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { autoTable } from "jspdf-autotable";
import { jsPDF } from "jspdf";

import "./style.css";
import ChartComponent from "../Chart/Chart";
import EventChart from "../EventChart/EventChart";
import HobacareVitalDetail from "../HobacareVitalDetail/HobacareVitalDetail";

const VitalDetail = () => {
	const { sidebarOpen, bedData } = useContext(DasboardContext);
	const { id } = useParams();
	const [events, setEvents] = useState([]);

	const formatDate = (isoString) => {
		if (!isoString) return "-";

		try {
			const date = new Date(isoString);

			// Geçersiz tarih kontrolü
			if (isNaN(date.getTime())) {
				return isoString; // Geçersizse orijinal değeri döndür
			}

			return date.toLocaleString("tr-TR", {
				timeZone: "Europe/Istanbul", // Her zaman Türkiye saati
				year: "numeric",
				month: "long",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});
		} catch (error) {
			console.error("Tarih formatlama hatası:", error, isoString);
			return isoString; // Hata durumunda orijinal değeri döndür
		}
	};

	const formatDetails = (details) => {
		if (!details) return "";

		// "Above 80 bpm" -> "80 bpm'nin üzerinde" veya "Above 20 rpm" -> "20 rpm'nin üzerinde"
		if (details.toLowerCase().includes("above")) {
			const match = details.match(/(\d+)\s*(bpm|rpm)/i);
			if (match) {
				const number = match[1];
				const unit = match[2].toLowerCase();
				return `${number} ${unit}'nin üzerinde`;
			}
			return details.replace(/above/gi, "üzerinde");
		}

		// "Below 80 bpm" -> "80 bpm'in altında" veya "Below 20 rpm" -> "20 rpm'in altında"
		if (details.toLowerCase().includes("below")) {
			const match = details.match(/(\d+)\s*(bpm|rpm)/i);
			if (match) {
				const number = match[1];
				const unit = match[2].toLowerCase();
				return `${number} ${unit}'in altında`;
			}
			return details.replace(/below/gi, "altında");
		}

		return details;
	};

	const [statues, setStatues] = useState(null);
	const [startDatetime, setStartDatetime] = useState("");
	const [endDatetime, setEndDatetime] = useState("");
	const [residentId, setResidentId] = useState("");
	const [profile, setProfile] = useState("");
	const [loading, setLoading] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 20;
	const [alarmPage, setAlarmPage] = useState(1);
	const alarmPerPage = 20;
	const [thisDevice, setThisDevice] = useState([]);

	const getAlarmEventLabel = (item) => {
		if (item.occupancy === "inBed") return "Yatağa Girdi";
		if (item.occupancy === "outOfBed" || item.title === "Out Of Bed") return "Yataktan Çıktı";
		if (item.title === "High Heart Rate")
			return `Yüksek Kalp Atışı${item.details ? ` (${formatDetails(item.details)})` : ""}`;
		if (item.title === "Low Heart Rate")
			return `Düşük Kalp Atışı${item.details ? ` (${formatDetails(item.details)})` : ""}`;
		if (item.title === "High Respiration Rate")
			return `Yüksek Solunum${item.details ? ` (${formatDetails(item.details)})` : ""}`;
		if (item.title === "Low Respiration Rate")
			return `Düşük Solunum${item.details ? ` (${formatDetails(item.details)})` : ""}`;
		if (!item.occupancy && item.title === "Posture Change") {
			if (item.details?.includes("prevent pressure ulcers"))
				return "Bası yaralarını önlemek için pozisyon verin";
			return item.details || "Pozisyon Değişimi";
		}
		return item.occupancy || item.title || "Bilinmeyen Olay";
	};

	useEffect(() => {
		if (!bedData || bedData.length === 0) return;

		const found = bedData.find((item) => item.deviceID === id);
		setThisDevice(found);
	}, [bedData, id]);

	const fetchResidentId = async (token) => {
		const url = `https://eu.vitalerter.com/api/v1/devices/${id}`;

		try {
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			setResidentId(data?.resident?.id);
		} catch (error) {
			console.error("Error fetching resident id:", error);
		}
	};

	// Tek bir API çağrısıyla hem vitals hem profile verilerini al
	const fetchVitalsAndProfile = async (token) => {
		if (!residentId || !startDatetime || !endDatetime) {
			return;
		}

		// Tarihleri API formatına çevir
		const formattedStart = formatDateForAPI(startDatetime);
		const formattedEnd = formatDateForAPI(endDatetime);

		const params = new URLSearchParams({
			startDatetime: formattedStart,
			endDatetime: formattedEnd,
			allParameters: true,
		});

		const url = `https://eu.vitalerter.com/api/v1/reports/profile/${residentId}?${params.toString()}`;

		try {
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			console.log("Vitals & Profile", data);
			
			// Events için filtreleme
			const filteredEvents =
				data?.items?.filter(
					(item) =>
						(item?.occupancy && item.time) ||
						item?.type === "alert" ||
						item?.title === "Out Of Bed"
				) || [];
			setEvents(filteredEvents);
			setAlarmPage(1);
			
			// Hem statues hem profile aynı veriyi kullanıyor
			setStatues(data);
			setProfile(data);
		} catch (error) {
			console.error("Error fetching vitals and profile:", error);
			setStatues(null);
			setProfile(null);
			setEvents([]);
		}
	};

	// Tarih formatını ISO 8601'e çevir (API için)
	// Kullanıcının seçtiği yerel saati UTC'ye çevirir
	const formatDateForAPI = (dateString) => {
		if (!dateString) return "";
		try {
			// datetime-local veya date formatını Date objesine çevir
			// Bu otomatik olarak yerel saat olarak yorumlanır
			const date = new Date(dateString);
			
			if (isNaN(date.getTime())) {
				return dateString;
			}
			
			// toISOString() UTC formatında döndürür
			// Örnek: Türkiye'de 02:05 seçildiğinde -> UTC 23:05 (önceki gün) olarak gönderilir
			return date.toISOString();
		} catch (error) {
			console.error("Tarih formatlama hatası:", error);
			return dateString;
		}
	};

	// Tarih validasyonu
	const validateDates = () => {
		if (!startDatetime || !endDatetime) {
			return false;
		}
		const start = new Date(startDatetime);
		const end = new Date(endDatetime);
		return start <= end;
	};

	// İlk yüklemede residentId'yi çek
	useEffect(() => {
		const getResidentId = async () => {
			const jwtToken =
				"/9m2n37CxCoGq9wVW1Sm1+tMsZHhPgPoay2eESkrPpY28apaCXhP1yCQY9fhks4H";
			const url =
				"https://eu.vitalerter.com/api/v1/organizations/apiaccess/authenticate";

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${jwtToken}`,
					},
					body: JSON.stringify({ apiKey: jwtToken }),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				if (data) {
					await fetchResidentId(data);
				}
			} catch (error) {
				console.error("Error during authentication:", error);
			}
		};

		if (!residentId) {
			getResidentId();
		}
	}, [id]); // Sadece id değiştiğinde çalışsın

	// Tarihler veya residentId değiştiğinde veri çek
	useEffect(() => {
		// Tarih kontrolü
		if (!startDatetime || !endDatetime || !residentId) {
			if (!startDatetime || !endDatetime) {
				setStatues(null);
				setProfile(null);
				setEvents([]);
			}
			return;
		}

		// Tarih validasyonu
		if (!validateDates()) {
			console.warn("Başlangıç tarihi bitiş tarihinden sonra olamaz!");
			return;
		}

		const fetchData = async () => {
			setLoading(true);
			const jwtToken =
				"/9m2n37CxCoGq9wVW1Sm1+tMsZHhPgPoay2eESkrPpY28apaCXhP1yCQY9fhks4H";
			const url =
				"https://eu.vitalerter.com/api/v1/organizations/apiaccess/authenticate";

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${jwtToken}`,
					},
					body: JSON.stringify({ apiKey: jwtToken }),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				if (data) {
					await fetchVitalsAndProfile(data);
				}
			} catch (error) {
				console.error("Error during authentication:", error);
				setStatues(null);
				setProfile(null);
				setEvents([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDatetime, endDatetime, residentId]);

	const filteredUniqueHourlyData = useMemo(() => {
		if (!statues) return [];

		const uniqueHourlyData = [];
		const addedHours = new Set();

		statues.items.forEach((item) => {
			const date = new Date(item.time);
			const hourKey =
				date.getFullYear() +
				"-" +
				(date.getMonth() + 1) +
				"-" +
				date.getDate() +
				" " +
				date.getHours();

			if (!addedHours.has(hourKey)) {
				uniqueHourlyData.push(item);
				addedHours.add(hourKey);
			}
		});

		return uniqueHourlyData;
	}, [statues]);

	const totalPages = useMemo(() => {
		return Math.ceil(filteredUniqueHourlyData.length / itemsPerPage);
	}, [filteredUniqueHourlyData]);

	const paginatedData = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;

		return filteredUniqueHourlyData.slice(startIndex, endIndex);
	}, [currentPage, filteredUniqueHourlyData]);

	const goToPage = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const convertToCSV = (data) => {
		const allHeaders = data.reduce((acc, item) => {
			Object.keys(item).forEach((key) => {
				if (!acc.includes(key)) acc.push(key);
			});
			return acc;
		}, []);

		const rows = data.map((item) =>
			allHeaders.map((header) => item[header] || "").join(",")
		);

		return [allHeaders.join(","), ...rows].join("\n");
	};

	const downloadCSV = () => {
		const csv = convertToCSV(statues.items);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		if (link.download !== undefined) {
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", "data.csv");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const downloadPDF = () => {
		if (!statues || !statues.items) return;

		const doc = new jsPDF("l", "pt", "a4");
		doc.setFontSize(14);
		doc.text("Vital Verileri", 40, 40);

		const tableColumn = [
			"Zaman",
			"HR",
			"RR",
			"HRV",
			"SV",
			"Hareket",
			"Rahatlık",
			"Yakındaki Kullanıcılar",
		];

		const tableRows = statues.items.map((item) => [
			formatDate(item.time),
			item.hr ?? "-",
			item.rr ?? "-",
			item.hrv ?? "-",
			item.sv ?? "-",
			item.movement ?? "-",
			item.relax ?? "-",
			item.usersNearby && item.usersNearby.length > 0
				? item.usersNearby.join(", ")
				: "Boş",
		]);

		autoTable(doc, {
			head: [tableColumn],
			body: tableRows,
			startY: 60,
			styles: { fontSize: 9 },
			headStyles: { fillColor: [22, 160, 133] },
		});

		doc.save("vital-verileri.pdf");
	};

	// Hobacare (Viva03D) cihazları için VitalAlerter API'si çalışmaz —
	// ayrı bileşen üzerinden Hobacare API'sine yönlendir.
	if (thisDevice?.brand === "Viva03D") {
		return <HobacareVitalDetail />;
	}

	return (
		<>
			<Breadcrumb />
			<div
				className={`device-wrapper component-wrapper ${
					sidebarOpen ? "" : "close"
				}`}
			>
				<div className="vital-info">
					<p>
						Oda Adı: <strong>{thisDevice.roomName}</strong>
					</p>
					<p>|</p>
					<p>
						Süit Adı: <strong>{thisDevice.suiteName}</strong>
					</p>
				</div>
				<div className="date-picker-container">
					<div>
						<label>Başlangıç Tarihi ve Saati:</label>
						<Form.Control
							type="datetime-local"
							value={startDatetime}
							onChange={(e) => {
								const dateValue = e.target.value;
								if (dateValue) {
									setStartDatetime(dateValue);
								} else {
									setStartDatetime("");
								}
							}}
							max={endDatetime || undefined}
						/>
					</div>

					<div>
						<label>Bitiş Tarihi ve Saati:</label>
						<Form.Control
							type="datetime-local"
							value={endDatetime}
							onChange={(e) => {
								const dateValue = e.target.value;
								if (dateValue) {
									setEndDatetime(dateValue);
								} else {
									setEndDatetime("");
								}
							}}
							min={startDatetime || undefined}
						/>
					</div>
					{loading && (
						<p style={{ color: "#16a085", marginTop: "10px" }}>
							Veriler yükleniyor...
						</p>
					)}
					{startDatetime && endDatetime && !validateDates() && (
						<p style={{ color: "#e74c3c", marginTop: "10px" }}>
							⚠️ Başlangıç tarihi bitiş tarihinden sonra olamaz!
						</p>
					)}
				</div>

				<div className="download-wrapper">
					<Button className="download-btn" onClick={downloadCSV}>
						Verileri İndir (Excel)
					</Button>
					<Button className="download-btn" onClick={downloadPDF}>
						Verileri İndir (PDF)
					</Button>
				</div>

				<Tabs
					defaultActiveKey="home"
					id="uncontrolled-tab-example"
					className="mb-3"
				>
					<Tab eventKey="home" title="Vital Değerler">
						{statues && statues.items && statues.items.length > 0 && (
							<ChartComponent data={statues.items} />
						)}

						{statues ? (
							<>
								<Table
									striped
									bordered
									hover
									responsive
									className="alarm-table"
								>
									<thead>
										<tr>
											<th>Zaman</th>
											<th>HR</th>
											<th>RR</th>
											<th>HRV</th>
											<th>SV</th>
											<th>Hareket</th>
											<th>Rahatlık</th>
											<th>Yakındaki Kullanıcılar</th>
										</tr>
									</thead>
									<tbody>
										{paginatedData.map((item, index) => (
											<tr key={index}>
												<td>{formatDate(item.time)}</td>
												<td>{item.hr}</td>
												<td>{item.rr}</td>
												<td>{item.hrv}</td>
												<td>{item.sv}</td>
												<td>{item.movement}</td>
												<td>{item.relax}</td>
												<td>
													{item.usersNearby?.length > 0
														? item.usersNearby.join(", ")
														: "Boş"}
												</td>
											</tr>
										))}
									</tbody>
								</Table>

								<div className="pagination-controls">
									<Button
										onClick={() => goToPage(currentPage - 1)}
										disabled={currentPage === 1}
									>
										Önceki
									</Button>
									<span>
										{currentPage} / {totalPages}
									</span>
									<Button
										onClick={() => goToPage(currentPage + 1)}
										disabled={currentPage === totalPages}
									>
										Sonraki
									</Button>
								</div>
							</>
						) : (
							<p>Lütfen tarih aralığı seçiniz...</p>
						)}
					</Tab>
					<Tab eventKey="profile" title="Alarm Bilgileri">
						{profile && profile.items && profile.items.length > 0 && (
							<EventChart data={profile.items} />
						)}
						{statues ? (
							<>
								<Table
									striped
									bordered
									hover
									responsive
									className="alarm-table"
								>
									<thead>
										<tr>
											<th>Sıra</th>
											<th>Zaman</th>
											<th>Olay Türü</th>
										</tr>
									</thead>
									<tbody>
										{events
											.slice(
												(alarmPage - 1) * alarmPerPage,
												alarmPage * alarmPerPage
											)
											.map((item, index) => (
											<tr key={index}>
												<td>{(alarmPage - 1) * alarmPerPage + index + 1}</td>
												<td>{formatDate(item.time)}</td>
												<td>{getAlarmEventLabel(item)}</td>
											</tr>
										))}
									</tbody>
								</Table>
								{events.length > alarmPerPage && (
									<div className="pagination-controls">
										<Button
											onClick={() => setAlarmPage((p) => Math.max(1, p - 1))}
											disabled={alarmPage === 1}
										>
											Önceki
										</Button>
										<span>
											{alarmPage} / {Math.ceil(events.length / alarmPerPage)}
										</span>
										<Button
											onClick={() =>
												setAlarmPage((p) =>
													Math.min(Math.ceil(events.length / alarmPerPage), p + 1)
												)
											}
											disabled={alarmPage >= Math.ceil(events.length / alarmPerPage)}
										>
											Sonraki
										</Button>
									</div>
								)}
							</>
						) : (
							<p>Lütfen tarih aralığı seçiniz...</p>
						)}
					</Tab>
				</Tabs>
			</div>
		</>
	);
};

export default VitalDetail;
